/**
 * SMS Notification Service for Admin Panel
 * Sends SMS notifications when content is approved
 */

// MNotify API Configuration
const MNOTIFY_API_KEY = import.meta.env.VITE_MNOTIFY_API_KEY || ''
const MNOTIFY_SENDER_ID = import.meta.env.VITE_MNOTIFY_SENDER_ID || 'FaithYoke'
const MNOTIFY_API_URL = 'https://api.mnotify.com/api/sms/quick'

export interface SMSResult {
  success: boolean
  messageId?: string
  error?: string
}

/**
 * Format phone number for MNotify (Ghana format)
 * Accepts: 0240144723, 240144723, +233240144723
 * Returns: 233240144723 (without + sign)
 */
export function formatPhoneNumber(phone: string): string {
  // Remove all non-digit characters
  let cleaned = phone.replace(/\D/g, '')

  // If starts with 0, remove it and add country code
  if (cleaned.startsWith('0')) {
    cleaned = '233' + cleaned.substring(1)
  }

  // If doesn't start with country code, add it
  if (!cleaned.startsWith('233')) {
    cleaned = '233' + cleaned
  }

  // MNotify expects without + sign
  return cleaned
}

/**
 * Send SMS via MNotify
 */
export async function sendSMS(
  to: string,
  message: string
): Promise<SMSResult> {
  try {
    if (!MNOTIFY_API_KEY) {
      console.warn('⚠️  MNotify API key not configured - SMS not sent')
      return {
        success: false,
        error: 'SMS service not configured'
      }
    }

    const recipient = formatPhoneNumber(to)
    console.log(`📤 Sending SMS to ${recipient} via MNotify...`)

    const payload = {
      recipient: [recipient],
      sender: MNOTIFY_SENDER_ID,
      message: message,
      is_schedule: false,
      schedule_date: ''
    }

    const url = `${MNOTIFY_API_URL}?key=${MNOTIFY_API_KEY}`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('MNotify API Error:', errorText)

      return {
        success: false,
        error: `MNotify error: ${response.status}`
      }
    }

    const data = await response.json()

    if (data.code === '2000' || data.status === 'success') {
      console.log('✅ SMS sent successfully via MNotify')

      return {
        success: true,
        messageId: data.summary?._id || data._id
      }
    } else {
      console.error('❌ MNotify returned error:', data)

      return {
        success: false,
        error: data.message || data.error || 'Failed to send SMS'
      }
    }
  } catch (error: any) {
    console.error('❌ Failed to send SMS via MNotify:', error)

    return {
      success: false,
      error: error.message || 'Network error'
    }
  }
}

/**
 * Send prayer request approval notification
 */
export async function sendPrayerApprovalSMS(
  phone: string,
  prayerTitle: string,
  userName: string
): Promise<SMSResult> {
  const message = `Hi ${userName}, your prayer request "${prayerTitle}" has been approved and is now shared with your church. The congregation is praying for you. - FaithYoke`

  return sendSMS(phone, message)
}

/**
 * Send testimony approval notification
 */
export async function sendTestimonyApprovalSMS(
  phone: string,
  testimonyTitle: string,
  userName: string
): Promise<SMSResult> {
  const message = `Hi ${userName}, your testimony "${testimonyTitle}" has been approved and is now shared with your church. Thank you for sharing how God has moved in your life! - FaithYoke`

  return sendSMS(phone, message)
}

/**
 * Send rejection notification with reason
 */
export async function sendRejectionSMS(
  phone: string,
  contentType: 'prayer' | 'testimony',
  contentTitle: string,
  userName: string,
  reason?: string
): Promise<SMSResult> {
  const typeLabel = contentType === 'prayer' ? 'prayer request' : 'testimony'
  const reasonText = reason ? ` Reason: ${reason}` : ''

  const message = `Hi ${userName}, your ${typeLabel} "${contentTitle}" was not approved for sharing.${reasonText} Please contact church leadership if you have questions. - FaithYoke`

  return sendSMS(phone, message)
}

/**
 * Send counselor assignment notification
 */
export async function sendCounselorAssignmentSMS(
  counselorPhone: string,
  counselorName: string,
  category: string,
  urgency: string
): Promise<SMSResult> {
  const message = `Hi ${counselorName}, a new ${urgency} priority counseling request (${category}) has been assigned to you. Please check the admin dashboard to review details and contact the member. - FaithYoke`

  return sendSMS(counselorPhone, message)
}

/**
 * Send notification to user when counselor is assigned
 */
export async function sendUserCounselorAssignedSMS(
  userPhone: string,
  userName: string,
  counselorName: string
): Promise<SMSResult> {
  const message = `Hi ${userName}, your counseling request has been assigned to ${counselorName}. They will contact you soon. If urgent, please call the church office. - FaithYoke`

  return sendSMS(userPhone, message)
}
